import { PsHttpService } from '@patsnap/tahiti-core';
import type { PatSnapCommonResponseData } from '@patsnap/tahiti-core/common/ps-http';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';

export interface IPatentComment {
    comments: any[];
    comment_count: number;
}

export interface ILiteratureAuthor {
    normalized_master_entity_id: string;
    name: string;
    normalized_name: string;
    normalized_display_name: string;
    is_startup: boolean;
    author_sequence_number: string;
    original_author: string;
    original_affiliation: string;
    display_author: string;
    display_affiliation: string;
    is_trustable: boolean;
    normalized_id: string;
}

export interface ILiteratureTopic {
    original_id: string;
    normalized_id: string;
    normalized_display_name: string;
    normalized_weight: number;
    original_index: number;
}

export interface ILiteratureJournal {
    id: string;
    name: string;
}

export interface ILiteratureOrganization {
    normalized_display_name: string;
    normalized_name: string;
    normalized_id: string;
}

export interface ILiteratureWebsite {
    source_type: number;
    source_url: string;
}

export interface ILiteraturePublishTime {
    time_ts: number;
    time_trust_code: number;
}
export interface ILiterature {
    PAPER_ID: string;
    DOC_ID: number;
    TITLE: string;
    ABSTRACT?: string;
    AUTHORS: ILiteratureAuthor[];
    ORGANIZATIONS?: ILiteratureOrganization[];
    JOURNAL?: ILiteratureJournal;
    PUBLISH_TIME?: ILiteraturePublishTime;
    CITATION_COUNT?: number;
    TOPICS?: ILiteratureTopic[];
    WEBSITE?: ILiteratureWebsite[];
    PDF?: any[];
    READ_STATUS?: boolean;
    WS_CREATED_AT?: string;
    [key: string]: any;
}

export interface IPatent {
    PATENT_ID: string;
    PN: string;
    TITLE: string;
    TITLE_LANG?: string;
    TITLE_TRAN?: string;
    TITLE_TRAN_LANG?: string;
    ANCS: string[];
    APD: string;
    PBD: string;
    WS_CREATED_AT?: string;
    LEGAL_STATUS?: number[];
    COMMENT?: IPatentComment;
    ADC?: any[];
    IPC?: any[];
    FAMILY_COUNTRY?: string;
    HAS_LICENSE?: boolean;
    HAS_STANDARD?: boolean;
    HAS_LITIGATION?: boolean;
    PATENT_COLOUR?: string;
    read_status?: boolean;
    [key: string]: any;
}

export interface IPatentResponse {
    num: number;
    total: number;
    page: number;
    limit: number;
    current_sort?: string;
    visible_fields?: string[];
    freeze_count?: number;
    patents_data: IPatent[];
}

export interface ILiteratureResponse {
    num: number;
    total: number;
    page: number;
    limit: number;
    current_sort?: string;
    visible_fields?: string[];
    available_fields?: string[];
    freeze_count?: number;
    data_edit?: boolean;
    custom_fields_add?: boolean;
    details: ILiterature[];
    comment?: {
        read: boolean;
        delete: boolean;
    };
}

export interface IWSTaskListParams {
    type: string;
    page: number;
    limit: number;
    status: string;
}

export interface IWSTaskListResponse {
    page: number;
    limit: number;
    total: number;
    rows: any[];
}

export interface WorkspaceApi {
    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort?: string): Promise<IPatentResponse>;
    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort?: string, dataType?: string): Promise<ILiteratureResponse>;
    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void>;
    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string): Promise<void>;
    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse>;
}

@injectable()
export class WorkspaceApiImpl extends PsHttpService implements WorkspaceApi {
    get options() {
        return {
            baseURL: `${import.meta.env.VITE_WORKSPACE_SERVICE_URI}/workspace`,
            headers: {
                'X-Api-Version': '2.0',
            },
        }
    }

    getWorkspacePatents(spaceId: string, folderId: string, page: number, limit: number, sort: string = 'wtasc'): Promise<IPatentResponse> {
        return this.post<PatSnapCommonResponseData<IPatentResponse>>({
            url: `/web/${spaceId}/folder/${folderId}/patents`,
            data: {
                page,
                sort,
                view_type: 'standardlist',
            },
        }).then((res) => {
            return res.data as IPatentResponse;
        }).catch(() => {
            return {
                num: 0,
                total: 0,
                page,
                limit,
                patents_data: [],
            } as IPatentResponse;
        });
    }

    getWorkspaceData(spaceId: string, folderId: string, page: number, limit: number, sort: string = 'wtasc', dataType = 'PAPER'): Promise<ILiteratureResponse> {
        return this.post<PatSnapCommonResponseData<ILiteratureResponse>>({
            url: `/web/${spaceId}/folder/${folderId}/data`,
            data: {
                page,
                sort,
                data_type: dataType,
                view_type: 'tablelist',
            },
        }).then((res) => {
            return res.data as ILiteratureResponse;
        }).catch(() => {
            return {
                num: 0,
                total: 0,
                page,
                limit,
                details: [],
            } as ILiteratureResponse;
        });
    }

    deleteWorkspacePatents(spaceId: string, folderId: string, patentIds: string[]): Promise<void> {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: `/web/space/${spaceId}/folder/${folderId}/delete-patents`,
            data: {
                family_type: null,
                patent_ids: patentIds,
                refiner: {},
                select_mode: 'INCLUDE',
                sort: 'wtasc',
            },
        }).then(() => {

        });
    }

    deleteWorkspaceData(folderId: string, docIds: string[], dataType: string = 'PAPER'): Promise<void> {
        return this.delete<PatSnapCommonResponseData<void>>({
            url: `/web/folder/${folderId}/delete-data`,
            data: {
                data_type: dataType,
                doc_ids: docIds,
                refiner: {},
                select_mode: 'INCLUDE',
                sort: 'wtasc',
            },
        }).then(() => {
        });
    }

    getTaskList(params: IWSTaskListParams): Promise<IWSTaskListResponse> {
        return this.get<PatSnapCommonResponseData<IWSTaskListResponse>>({
            url: '/web/task-center/list',
            params: {
                type: params.type,
                page: params.page,
                limit: params.limit,
                status: params.status,
            },
        }).then((res) => {
            return res.data as IWSTaskListResponse;
        }).catch(() => {
            return {
                page: params.page,
                limit: params.limit,
                total: 0,
                rows: [],
            } as IWSTaskListResponse;
        });
    }
}

export const WorkspaceApi = Symbol('WorkspaceApi');
